// Caja del día de Farmacia Interna (Fase 3-4, 2026-09-11) — solo lectura
// sobre payments/invoices, mismo motor que /admin/caja del CRM
// (lib/billing.ts:cajaDelDiaFarmacia). Los pendientes de facturar a un
// convenio se gestionan en la pestaña Convenios (misma lista, con la acción).
import type { JSX } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { cajaDelDiaFarmacia, type CajaDiaFarmacia } from '@/lib/farmaciaInterna';

function formatoUsd(cents: number): string {
  return (cents / 100).toLocaleString('es-EC', { style: 'currency', currency: 'USD' });
}

function formatoHora(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit' });
}

export function CajaFarmacia(): JSX.Element {
  const { t } = useTranslation();
  const [caja, setCaja] = useState<CajaDiaFarmacia>();

  useEffect(() => {
    void cajaDelDiaFarmacia().then(setCaja);
  }, []);

  if (caja === undefined) {
    return <p className="text-sm text-hce-muted">{t('farmaciaApp.cargando')}</p>;
  }

  const pendientesSinFacturar = caja.porCobrarConvenio.filter((p) => !p.facturadoConvenio).length;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <div className="rounded-lg border border-border p-4">
          <p className="text-xs text-hce-muted">{t('farmaciaApp.caja.cobradoHoy')}</p>
          <p className="text-xl font-semibold text-hce-ink">{formatoUsd(caja.cobradoHoyCents)}</p>
        </div>
        <div className="rounded-lg border border-border p-4">
          <p className="text-xs text-hce-muted">{t('farmaciaApp.caja.cobros')}</p>
          <p className="text-xl font-semibold text-hce-ink">{caja.items.length}</p>
        </div>
        <div className="rounded-lg border border-border p-4">
          <p className="text-xs text-hce-muted">{t('farmaciaApp.caja.pendientesConvenio')}</p>
          <p className="text-xl font-semibold text-hce-ink">{pendientesSinFacturar}</p>
        </div>
      </div>

      <div className="space-y-2">
        <p className="text-sm font-semibold text-hce-ink">{t('farmaciaApp.caja.listadoTitulo')}</p>
        {caja.items.length === 0 ? (
          <p className="text-sm text-hce-muted">{t('farmaciaApp.caja.vacio')}</p>
        ) : (
          <div className="divide-y divide-border rounded-lg border border-border">
            {caja.items.map((i) => (
              <div key={i.paymentId} className="flex items-center justify-between gap-3 px-3 py-2.5 text-sm">
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-hce-ink">{i.contactName || t('farmaciaApp.caja.sinNombre')}</span>
                  <span className="block text-xs text-hce-muted">
                    {i.comoPaga} · {formatoHora(i.at)}
                  </span>
                </span>
                <span className="shrink-0 font-mono text-sm text-hce-ink">{formatoUsd(i.amountCents)}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
